import axios from 'axios';
import { CLOUDFRONT_BASE_URLS, CLOUDFRONT_PRIVATE_BASE_URLS } from './cloudfront-urls';

export interface FileUploadOptions {
  /**
   * Path/folder within the S3 bucket where the file should be stored
   * Optional - if not provided, backend will use default location
   */
  folderPath?: string;
  /**
   * Optional API URL to get signed URL from backend. If not provided, will use environment variable.
   */
  apiUrl?: string;
  /**
   * Whether the file should be uploaded to private bucket instead of public
   */
  isPrivate?: boolean;
  /**
   * Optional file name prefix
   */
  prefix?: string;
  /**
   * Optional file name suffix
   */
  suffix?: string;
  /**
   * Whether to include timestamp in file name (default: true)
   */
  includeTimestamp?: boolean;
  /**
   * Optional additional parameters for S3 path generation
   */
  params?: Record<string, string | number>;
  /**
   * Optional callbacks for progress/status updates
   */
  onProgress?: (progress: number) => void;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  /**
   * Whether to skip the temp bucket
   */
  skipTempBucket?: boolean;

  /**
   * Whether to use S3 accelerated endpoints for faster uploads
   */
  useAcceleratedEndpoint?: boolean;

  /**
   * File size in bytes - used to determine storage class
   */
  fileSize?: number;

  /**
   * Axios cancel token source for cancelling upload requests
   */
  cancelTokenSource?: any;

  /**
   * Optional backend namespace to target specific API routes.
   * - 'control-panel' -> /api/control-panel/...
   * - 'company'       -> /api/company/...
   * If omitted, the namespace is inferred from the apiUrl.
   */
  namespace?: 'control-panel' | 'company' | string;
}

export interface FileUploadResult {
  /**
   * URL of the uploaded file
   */
  fileUrl: string;
  /**
   * S3 key of the uploaded file
   */
  key: string;
  /**
   * CloudFront URL base
   */
  cloudFront: string;
}

export interface FileTransferOptions {
  /**
   * API URL to transfer files from temp bucket. If not provided, will use environment variable.
   */
  apiUrl?: string;
  /**
   * Files to transfer from temp bucket
   */
  filesToTransfer: {
    /**
     * Source key in temp bucket
     */
    sourceKey: string;
    /**
     * Optional source bucket (defaults based on isPrivate flag)
     */
    sourceBucket?: string;
    /**
     * Optional destination key (defaults to sourceKey if not provided)
     */
    destinationKey?: string;
    /**
     * Optional destination bucket (defaults based on isPrivate flag)
     */
    destinationBucket?: string;
    /**
     * Whether the file is private
     */
    isPrivate?: boolean;
    /**
     * Optional ACL setting (defaults to 'public-read')
     */
    acl?: string;
    /**
     * File size in bytes - used to determine storage class during transfer
     */
    fileSize?: number;
  }[];
  /**
   * Optional callbacks for progress/status updates
   */
  onStart?: () => void;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export interface FileTransferResult {
  /**
   * Results of each file transfer
   */
  transferResults: {
    /**
     * Whether the transfer was successful
     */
    success: boolean;
    /**
     * Source key in temp bucket
     */
    sourceKey: string;
    /**
     * Destination key in permanent bucket
     */
    destinationKey: string;
    /**
     * URL of the transferred file
     */
    url?: string;
    /**
     * Error message if transfer failed
     */
    error?: string;
  }[];
  /**
   * Number of successful transfers
   */
  successCount: number;
  /**
   * Number of failed transfers
   */
  failureCount: number;
}

/**
 * Uploads a file to S3 using a signed URL from the backend
 * This is a utility function that can be used in class components
 */
export const uploadFileToS3 = async (
  file: File,
  options: FileUploadOptions
): Promise<FileUploadResult | null> => {
  try {
    if (options.onStart) {
      options.onStart();
    }

    // Use environment variables for API URL if not provided
    const apiBaseUrl = options.apiUrl;

    if (!apiBaseUrl) {
      console.error('[uploadFileToS3] No API URL found in options or env variables');
      throw new Error('API URL is not defined');
    }

    // Prepare file metadata
    const fileExtension = file.name.split('.').pop();
    const randomId = parseInt(String(Math.random() * 1000000000)) * 10;
    const fileName = `${options.prefix || ''}${Date.now()}_${randomId}${options.suffix || ''}.${fileExtension}`;

    // Create content object for signed URL request
    const contentToUpload = {
      name: fileName,
      ...(options.folderPath ? { folderPath: options.folderPath } : {}), // Only include folderPath if it's not empty
      extension: fileExtension,
      contentType: file.type,
      prefix: options.prefix,
      suffix: options.suffix,
      params: options.params,
      includeTimestamp: options.includeTimestamp !== false,
      private: options.isPrivate,
      skipTempBucket: options.skipTempBucket || false,
      useAcceleratedEndpoint: options.useAcceleratedEndpoint || false,
      fileSize: file.size
    };

    // Get signed URL from backend
    // Prefer explicit namespace when provided; otherwise fall back to heuristic.
    let endpoint = '/api/link/get-signed-url/v2';
    if (options?.namespace === 'control-panel') {
      endpoint = '/api/control-panel/get-signed-url/v2';
    } else if (options?.namespace === 'company') {
      endpoint = '/api/company/get-signed-url/v2';
    } else if (apiBaseUrl.includes('control-panel')) {
      endpoint = '/api/control-panel/get-signed-url/v2';
    } else if (apiBaseUrl.includes('company')) {
      endpoint = '/api/company/get-signed-url/v2';
    }

    const requestUrl = `${apiBaseUrl}${endpoint}`;

    // Get signed URL from backend
    let response;
    try {
      response = await axios.post(
        requestUrl,
        { contentToUpload: [contentToUpload] },
        {
          cancelToken: options.cancelTokenSource?.token
        }
      );
    } catch (err: unknown) {
      console.error('[uploadFileToS3] Request failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      throw new Error(`Failed to get signed URL: ${errorMessage}`);
    }

    // Extract data from the known response format
    const uploadData = response.data.data.signedUrls[0];

    const { uploadURL, Key, cloudFront } = uploadData;

    // Upload file directly to S3 using the signed URL
    try {
      // Use axios with custom configuration to avoid authorization conflicts
      // Create a new axios instance without interceptors to avoid automatic auth headers
      const uploadAxios = axios.create();

      const uploadResponse = await uploadAxios.put(uploadURL, file, {
        headers: {
          'Content-Type': file.type // Set the correct Content-Type for the file
          // Explicitly remove any authorization headers
        },
        cancelToken: options.cancelTokenSource?.token,
        onUploadProgress: (progressEvent) => {
          if (options.onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            options.onProgress(progress);
          }
        },
        // Transform request to ensure no unwanted headers are added
        transformRequest: [
          (data, headers) => {
            // Remove any authorization headers that might be added by interceptors
            delete headers.authorization;
            delete headers.Authorization;
            delete headers['X-Amz-Algorithm'];
            delete headers['x-amz-algorithm'];
            return data;
          }
        ]
      });

      if (uploadResponse.status !== 200) {
        console.error(
          '[uploadFileToS3] Upload failed:',
          uploadResponse.status,
          uploadResponse.statusText
        );
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }
    } catch (error: any) {
      console.error('[uploadFileToS3] Upload failed:', error);
      if (error.response) {
        console.error('[uploadFileToS3] Error details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data || error.response.statusText,
          headers: error.response.headers
        });

        // Try to parse S3 error response for better debugging
        if (error.response.data && typeof error.response.data === 'string') {
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(error.response.data, 'text/xml');
            const codeElement = xmlDoc.getElementsByTagName('Code')[0];
            const messageElement = xmlDoc.getElementsByTagName('Message')[0];
            if (codeElement && messageElement) {
              console.error('[uploadFileToS3] S3 Error:', {
                code: codeElement.textContent,
                message: messageElement.textContent
              });
            }
          } catch (parseError) {
            console.error('[uploadFileToS3] Could not parse S3 error response:', parseError);
          }
        }
      }
      throw error;
    }

    // Return the file URL (CloudFront URL + Key)
    const fileUrl = `${cloudFront}${Key}`.split('?')[0];

    if (options.onComplete) {
      options.onComplete();
    }

    return {
      fileUrl,
      key: Key,
      cloudFront
    };
  } catch (err) {
    console.error('[uploadFileToS3] Error caught:', err);
    const error = err instanceof Error ? err : new Error('Unknown error occurred');

    if (options.onError) {
      console.error('[uploadFileToS3] Calling onError callback with:', error);
      options.onError(error);
    }

    return null;
  }
};

/**
 * Transfers files from temporary bucket to permanent bucket
 * Use this function after form submission when files have been uploaded to temp bucket
 */
export const transferFilesFromTempBucket = async (
  options: FileTransferOptions
): Promise<FileTransferResult | null> => {
  try {
    if (options.onStart) {
      options.onStart();
    }

    // Use environment variables for API URL if not provided
    const apiBaseUrl =
      options.apiUrl ||
      process.env.NEXT_PUBLIC_NODE_API_URL ||
      process.env.VITE_APP_NODE_API_ENDPOINT ||
      (window as any).NEXT_PUBLIC_NODE_API_URL ||
      (window as any).VITE_APP_NODE_API_ENDPOINT;

    if (!apiBaseUrl) {
      console.error('[transferFilesFromTempBucket] No API URL found in options or env variables');
      throw new Error('API URL is not defined');
    }

    // Determine the correct endpoint based on API URL
    let endpoint = '/api/link/transfer-from-temp-bucket';
    if (apiBaseUrl.includes('control-panel')) {
      endpoint = '/api/control-panel/transfer-from-temp-bucket';
    } else if (apiBaseUrl.includes('company')) {
      endpoint = '/api/company/transfer-from-temp-bucket';
    }

    const requestUrl = `${apiBaseUrl}${endpoint}`;

    if (options.onProgress) {
      options.onProgress(25);
    }

    // Prepare payload for transfer request
    const payload = {
      filesToTransfer: options.filesToTransfer
    };

    // Call backend to transfer files
    let response;
    try {
      response = await axios.post(requestUrl, payload);

      if (options.onProgress) {
        options.onProgress(75);
      }
    } catch (err: unknown) {
      console.error('[transferFilesFromTempBucket] Transfer request failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      throw new Error(`Failed to transfer files: ${errorMessage}`);
    }

    // Extract data from response
    const transferResult = response.data.data;

    if (options.onProgress) {
      options.onProgress(100);
    }

    if (options.onComplete) {
      options.onComplete();
    }

    return {
      transferResults: transferResult.transferResults || [],
      successCount: transferResult.successCount || 0,
      failureCount: transferResult.failureCount || 0
    };
  } catch (err) {
    console.error('[transferFilesFromTempBucket] Error caught:', err);
    const error = err instanceof Error ? err : new Error('Unknown error occurred');

    if (options.onError) {
      console.error('[transferFilesFromTempBucket] Calling onError callback with:', error);
      options.onError(error);
    }

    return null;
  }
};
// ! DEPRECATED 24 Feb 2026
// const S3_PUBLIC_BUCKET_URLS_V4 = {
//   production: 'https://prod-lua-public-x9p2q6w4-v4.s3.eu-west-1.amazonaws.com/',
//   staging: 'https://staging-lua-public-c4e8m1z7-v4.s3.eu-west-1.amazonaws.com/',
//   development: 'https://local-lua-public-f7b9d3a2-v4.s3.eu-west-1.amazonaws.com/'
// };

// deprecated
// const S3_PUBLIC_BUCKET_URLS_V3 = {
//   production: 'https://prod-lua-public-x9p2q6w4.s3.eu-west-1.amazonaws.com/',
//   staging: 'https://staging-lua-public-c4e8m1z7.s3.eu-west-1.amazonaws.com/',
//   development: 'https://local-lua-public-f7b9d3a2.s3.eu-west-1.amazonaws.com/'
// };

export const getS3PublicUrl = (key: string, isPrivate = false) => {
  //add check for if the key is already a url
  if (!key) {
    console.warn('S3 key is required to construct URL');
    return '';
  }
  if (key.startsWith('http')) {
    return key;
  }
  const viteEnv = typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ENV;
  const environment = viteEnv || process.env.NEXT_PUBLIC_ENV;

  if (isPrivate) {
    return `${CLOUDFRONT_PRIVATE_BASE_URLS[environment as keyof typeof CLOUDFRONT_PRIVATE_BASE_URLS] ?? CLOUDFRONT_PRIVATE_BASE_URLS.staging}${key}`;
  }

  // ! DEPRECATED 24 Feb 2026
  //const useV4Bucket = key.startsWith('v4/');
  //const bucketUrls = useV4Bucket ? S3_PUBLIC_BUCKET_URLS_V4 : S3_PUBLIC_BUCKET_URLS_V3;
  // const bucketUrls = S3_PUBLIC_BUCKET_URLS_V4;
  // const bucketUrl = bucketUrls[environment as keyof typeof S3_PUBLIC_BUCKET_URLS_V4];
  const bucketUrl =
    CLOUDFRONT_BASE_URLS[environment as keyof typeof CLOUDFRONT_BASE_URLS] ??
    CLOUDFRONT_BASE_URLS.staging;
  // Remove leading slash from key if present to avoid double slashes
  const cleanKey = key.startsWith('/') ? key.slice(1) : key;

  return `${bucketUrl}${cleanKey}`;
};

// Helper function to extract S3 key from full URL
export const extractS3KeyFromUrl = (url: string) => {
  if (!url) return null;

  try {
    if (!url.startsWith('http')) {
      return url;
    }

    // Handle CloudFront/S3 URLs - extract the key part after the domain
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // Remove leading slash and return the key
    return pathname.startsWith('/') ? pathname.substring(1) : pathname;
  } catch (error) {
    console.error('Error extracting S3 key from URL:', error);
    return null;
  }
};
